import { bpsToPercentString } from '@/utils/bps';
import { isDividendErrorCode } from '@/api/handlers/resources/dividends/dividendsMapping';
import type { ErrorResponse } from '@/api/handlers/responses';
import type {
    CalculatorDirection,
    ChartAccount,
    DividendDeclarationSummary,
    DividendLine,
    DividendTotals,
    DividendsSummary,
    LocaleCode,
    Shareholder,
    ShareholderResidency,
    ShareholderType,
    TaxRegime,
} from '@/api/models';
import {
    formatGel,
    ledgerAccountCode,
    ledgerNaturalTetri,
} from '@/pages/accounting/books/chartOfAccounts/utils';
import {
    isoToDisplayDate,
    NIL_AMOUNT,
    resolveLabel,
} from '@/pages/accounting/statements/statementMeta';

/**
 * Pure rules and presentational config for Profit & Dividends.
 *
 * Everything derived lives here so the views stay render-only, and so the things
 * that must not be got wrong — the tie-out, the distributable-profit gate, and the
 * effective-rate figure an owner will make a decision on — are plain functions
 * with tests rather than JSX.
 */

// ── percentages ─────────────────────────────────────────────────────────────

/**
 * Basis points as display text (`1925` → `"19.25"`, `500` → `"5"`).
 *
 * Re-exported from `@/utils/bps` (BOS-162): four files carried their own copy of
 * this conversion, each with its own rounding, so one rate could read 19.25% on
 * the dividends register and 19.3% on the settings form.
 */
export const bpsToPercentText = bpsToPercentString;

export type PercentParseResult =
    | { ok: true; bps: number }
    | { ok: false; reason: 'empty' | 'invalid' | 'range' | 'precision' };

/**
 * Percent text → basis points, for the shareholder form's holding and
 * withholding fields.
 *
 * Parsed on the digit strings rather than with `parseFloat`, so `2.5` is
 * unambiguously 250 bp. More than two decimal places is finer than a basis
 * point and is **refused rather than rounded** — the same rule the money parser
 * applies to sub-tetri input, for the same reason.
 *
 * This lived in the settings screen's meta until BOS-90, whose rate inputs
 * became `NumberField`s that constrain precision themselves. The shareholder
 * form is now the only caller, so it lives here.
 */
export const parsePercentToBps = (input: string): PercentParseResult => {
    const trimmed = input.trim().replace(/\s|%/g, '');
    if (trimmed === '') {
        return { ok: false, reason: 'empty' };
    }
    if (!/^\d*(?:\.\d+)?$/.test(trimmed) || !/\d/.test(trimmed)) {
        return { ok: false, reason: 'invalid' };
    }
    const [wholeRaw = '', fracRaw = ''] = trimmed.split('.');
    if (fracRaw.length > 2 && /[1-9]/.test(fracRaw.slice(2))) {
        return { ok: false, reason: 'precision' };
    }
    const whole = wholeRaw === '' ? '0' : wholeRaw;
    const bps =
        Number(whole) * 100 + Number(fracRaw.slice(0, 2).padEnd(2, '0'));
    if (bps < 0 || bps > 10_000) {
        return { ok: false, reason: 'range' };
    }
    return { ok: true, bps };
};

/** i18n key for a regime's name — shared with the settings screen's copy. */
export const regimeLabelKey = (regime: TaxRegime): string =>
    `settings.regimes.${regime}.name`;

// ── shareholder rows ────────────────────────────────────────────────────────

export const SHAREHOLDER_TYPE_KEY: Record<ShareholderType, string> = {
    INDIVIDUAL: 'dividends.shareholderType.individual',
    COMPANY: 'dividends.shareholderType.company',
};

export const RESIDENCY_KEY: Record<ShareholderResidency, string> = {
    RESIDENT: 'dividends.residency.resident',
    NON_RESIDENT: 'dividends.residency.nonResident',
};

/** Shared header cell classes, matching the other accounting registers. */
export const REGISTER_TEXT_HEADER =
    'px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-muted';
export const REGISTER_NUMERIC_HEADER =
    'px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-muted';

/** A render-ready shareholder row. All derivation happens here. */
export type ShareholderRow = {
    id: string;
    index: number;
    name: string;
    identifier: string;
    typeKey: string;
    residencyKey: string;
    holdingText: string;
    /**
     * The rate that would actually apply on the register's `asOf` date. Nil when
     * no rate set governs that date — the register can then say what is stored
     * but not what it resolves to, and inventing a percentage would be worse.
     */
    withholdingText: string;
    /**
     * True when this holder carries a **stored** rate rather than taking the
     * entity default. Read off `withholdingTaxBps` being non-null, not off the
     * resolved rate differing from the default: a treaty rate that happens to
     * equal today's default is still a recorded decision, and a holder with no
     * decision recorded must not be flagged the day the default moves.
     */
    isRateOverridden: boolean;
    withholdingNote: string;
    citExempt: boolean;
};

export const toShareholderRows = (
    shareholders: Shareholder[],
    locale: LocaleCode,
): ShareholderRow[] =>
    shareholders.map((holder, index) => ({
        id: holder.id,
        index: index + 1,
        name: resolveLabel(holder.name, locale, holder.id),
        identifier: holder.identifier,
        typeKey: SHAREHOLDER_TYPE_KEY[holder.type],
        residencyKey: RESIDENCY_KEY[holder.residency],
        holdingText: `${bpsToPercentText(holder.holdingBps)}%`,
        withholdingText:
            holder.effectiveWithholdingTaxBps === null
                ? NIL_AMOUNT
                : `${bpsToPercentText(holder.effectiveWithholdingTaxBps)}%`,
        isRateOverridden: holder.withholdingTaxBps !== null,
        withholdingNote: holder.withholdingNote,
        citExempt: holder.citExempt,
    }));

/**
 * Whether the register's holdings total 100%.
 *
 * A warning, never a block: a register mid-edit legitimately does not add up, and
 * refusing to show anything until it does would be worse than saying so.
 */
export type HoldingCheck = {
    totalBps: number;
    totalText: string;
    isComplete: boolean;
    /** Signed difference from 100%, basis points. */
    differenceBps: number;
    differenceText: string;
};

export const checkHoldings = (totalBps: number): HoldingCheck => ({
    totalBps,
    totalText: `${bpsToPercentText(totalBps)}%`,
    isComplete: totalBps === 10_000,
    differenceBps: totalBps - 10_000,
    differenceText: `${totalBps > 10_000 ? '+' : ''}${bpsToPercentText(
        totalBps - 10_000,
    )}%`,
});

// ── summary ─────────────────────────────────────────────────────────────────

export type SummaryFigures = {
    retainedEarningsText: string;
    resultForPeriodText: string;
    distributableProfitText: string;
    declaredUnpaidText: string;
    citPayableText: string;
    whtPayableText: string;
    /** True when there is nothing left to distribute. */
    isExhausted: boolean;
};

export const toSummaryFigures = (
    summary: DividendsSummary,
): SummaryFigures => ({
    retainedEarningsText: formatGel(summary.retainedEarningsTetri),
    resultForPeriodText: formatGel(summary.resultForPeriodTetri),
    distributableProfitText: formatGel(summary.distributableProfitTetri),
    declaredUnpaidText:
        summary.declaredUnpaidDividendsTetri === 0
            ? NIL_AMOUNT
            : formatGel(summary.declaredUnpaidDividendsTetri),
    citPayableText:
        summary.corporateIncomeTaxPayableTetri === 0
            ? NIL_AMOUNT
            : formatGel(summary.corporateIncomeTaxPayableTetri),
    whtPayableText:
        summary.withholdingTaxPayableTetri === 0
            ? NIL_AMOUNT
            : formatGel(summary.withholdingTaxPayableTetri),
    isExhausted: summary.distributableProfitTetri <= 0,
});

// ── the tie-out ─────────────────────────────────────────────────────────────

export type TieOutFigure = {
    labelKey: string;
    slot: string;
    registerTetri: number;
    ledgerTetri: number;
    differenceTetri: number;
    agrees: boolean;
    accountCode: string;
    /**
     * Whether the ledger side could be read at all. `false` means the summary
     * names no account for this role (the chart has nothing tagged for it), or
     * it names one the loaded chart does not hold. There is then nothing to
     * compare against, so the row says so rather than reporting a difference
     * equal to the whole balance. `agrees` is forced true in that case so an
     * unknown ledger can never colour the screen as a disagreement.
     */
    isResolved: boolean;
};

export type DividendsTieOut = {
    figures: TieOutFigure[];
    /** False when **no** side could be resolved — nothing to compare at all. */
    isResolved: boolean;
    /** True only when every resolved side agrees to the tetri. */
    inAgreement: boolean;
};

/**
 * Sum the **natural** balance of one account — the balance in its own normal
 * direction, so a credit-normal payable reports what is owed as a positive
 * magnitude, the same sense the register carries its outstanding positions in.
 */
const naturalTetriFor = (
    accounts: ChartAccount[],
    accountId: string,
): number | null => ledgerNaturalTetri(accounts, accountId);

const toFigure = (
    labelKey: string,
    slot: string,
    registerTetri: number,
    accounts: ChartAccount[],
    /** Null when the chart carries no account tagged for this dividend role. */
    accountId: string | null,
): TieOutFigure => {
    const ledgerTetri =
        accountId === null ? null : naturalTetriFor(accounts, accountId);
    const accountCode =
        accountId === null ? '' : ledgerAccountCode(accounts, accountId);
    if (ledgerTetri === null) {
        return {
            labelKey,
            slot,
            registerTetri,
            ledgerTetri: 0,
            differenceTetri: 0,
            agrees: true,
            accountCode,
            isResolved: false,
        };
    }
    const differenceTetri = registerTetri - ledgerTetri;
    return {
        labelKey,
        slot,
        registerTetri,
        ledgerTetri,
        differenceTetri,
        agrees: differenceTetri === 0,
        accountCode,
        isResolved: true,
    };
};

/**
 * Compare what the register has accrued against what the ledger holds.
 *
 * This is the point of the screen, and BOS-112 is what made it real: with the
 * register live, both sides describe the same books. The accrued figures come
 * from the register's own summary — derived from declarations whose entry has
 * **posted** — and the ledger figures from the existing accounts query, so the
 * register is never allowed to certify itself: it names the accounts, it does
 * not supply their balances.
 *
 * Both sides of the dividends-payable row are the **net**. The wire's
 * `declaredUnpaidDividends` is what the entry credited to dividends payable, not
 * the gross it was declared at — subtracting the withholding from it, as this
 * did against the mock, would report a permanent difference of exactly that
 * withholding, in the wrong direction.
 *
 * A difference is not necessarily an error: paying a shareholder or remitting a
 * tax moves the ledger and the register never sees it. The screen reports the
 * difference and lets a bookkeeper read it, rather than calling it a failure.
 *
 * Returns `null` while the chart is still loading — an unknown ledger is not the
 * same as a disagreeing one, and must never be drawn as a difference.
 */
export const computeTieOut = (
    summary: DividendsSummary,
    accounts: ChartAccount[] | null,
): DividendsTieOut | null => {
    if (accounts === null) {
        return null;
    }

    const figures: TieOutFigure[] = [
        toFigure(
            'dividends.tieOut.dividendsPayable',
            'dividends-payable',
            summary.declaredUnpaidDividendsTetri,
            accounts,
            summary.dividendsPayableAccountId,
        ),
        toFigure(
            'dividends.tieOut.citPayable',
            'cit-payable',
            summary.corporateIncomeTaxPayableTetri,
            accounts,
            summary.corporateIncomeTaxPayableAccountId,
        ),
        toFigure(
            'dividends.tieOut.whtPayable',
            'wht-payable',
            summary.withholdingTaxPayableTetri,
            accounts,
            summary.withholdingTaxPayableAccountId,
        ),
    ];

    // All three rows or none: an unresolved row has no ledger side, and the bar
    // draws every row the same way. Showing it beside two real ones would put
    // "₾ 5.00 = ₾ 0.00" against an account that is not in the chart.
    const isResolved = figures.every((figure) => figure.isResolved);

    return {
        figures,
        isResolved,
        inAgreement: isResolved && figures.every((figure) => figure.agrees),
    };
};

// ── the calculator ──────────────────────────────────────────────────────────

/** The three ways the calculator can be driven, in the order it offers them. */
export const CALCULATOR_DIRECTIONS: {
    id: CalculatorDirection;
    labelKey: string;
    /** What the amount field means in this direction. */
    fieldLabelKey: string;
}[] = [
    {
        id: 'BY_CHARGE',
        labelKey: 'dividends.calculator.directions.byCharge',
        fieldLabelKey: 'dividends.calculator.fields.charge',
    },
    {
        id: 'BY_GROSS',
        labelKey: 'dividends.calculator.directions.byGross',
        fieldLabelKey: 'dividends.calculator.fields.gross',
    },
    {
        id: 'BY_NET',
        labelKey: 'dividends.calculator.directions.byNet',
        fieldLabelKey: 'dividends.calculator.fields.net',
    },
];

export const directionFieldLabelKey = (
    direction: CalculatorDirection,
): string =>
    CALCULATOR_DIRECTIONS.find((option) => option.id === direction)
        ?.fieldLabelKey ?? 'dividends.calculator.fields.charge';

/** The calculator's answer, pre-formatted. */
export type QuoteFigures = {
    chargeText: string;
    grossDividendText: string;
    citText: string;
    withholdingText: string;
    netText: string;
    effectiveRateText: string;
    /** True when no CIT applies at all — a zero-rate regime, or all-exempt. */
    isCitFree: boolean;
};

export const toQuoteFigures = (totals: DividendTotals): QuoteFigures => ({
    chargeText: formatGel(totals.chargeTetri),
    grossDividendText: formatGel(totals.grossDividendTetri),
    citText: totals.citTetri === 0 ? NIL_AMOUNT : formatGel(totals.citTetri),
    withholdingText:
        totals.withholdingTaxTetri === 0
            ? NIL_AMOUNT
            : formatGel(totals.withholdingTaxTetri),
    netText: formatGel(totals.netTetri),
    effectiveRateText: `${bpsToPercentText(totals.effectiveRateBps)}%`,
    isCitFree: totals.citTetri === 0,
});

// ── declaration rows ────────────────────────────────────────────────────────

/** A render-ready per-shareholder allocation row. */
export type AllocationRow = {
    shareholderId: string;
    name: string;
    holdingText: string;
    grossText: string;
    withholdingText: string;
    netText: string;
    citText: string;
    citExempt: boolean;
    isManual: boolean;
    isZero: boolean;
};

export const toAllocationRows = (
    lines: DividendLine[],
    locale: LocaleCode,
): AllocationRow[] =>
    lines.map((line) => ({
        shareholderId: line.shareholderId,
        name: resolveLabel(line.shareholderName, locale, line.shareholderId),
        holdingText: `${bpsToPercentText(line.holdingBps)}%`,
        grossText:
            line.grossTetri === 0 ? NIL_AMOUNT : formatGel(line.grossTetri),
        withholdingText:
            line.withholdingTaxTetri === 0
                ? NIL_AMOUNT
                : formatGel(line.withholdingTaxTetri),
        netText: line.netTetri === 0 ? NIL_AMOUNT : formatGel(line.netTetri),
        citText: line.citTetri === 0 ? NIL_AMOUNT : formatGel(line.citTetri),
        citExempt: line.citExempt,
        isManual: line.isManualAllocation,
        isZero: line.grossTetri === 0,
    }));

// ── declaration history ─────────────────────────────────────────────────────

export type DeclarationRow = {
    /** The entry's id — a declaration has no identity apart from it. */
    id: string;
    /**
     * ISO `YYYY-MM-DD`, alongside the formatted `dateText`. The audit link into
     * the Journal tab needs a window that contains the entry, and a declaration
     * stamps its entry with exactly this date — so the raw value has to survive
     * the trip to the row, not just the display string.
     */
    date: string;
    dateText: string;
    shareholderCount: number;
    chargeText: string;
    citText: string;
    netText: string;
    dueDateText: string;
    journalEntryId: string;
    /**
     * True while the declaration's entry is still a draft. It has been declared
     * but nothing has moved — none of these figures is in the ledger yet.
     */
    isUnposted: boolean;
};

export const toDeclarationRows = (
    declarations: DividendDeclarationSummary[],
): DeclarationRow[] =>
    declarations.map((declaration) => ({
        id: declaration.journalEntryId,
        date: declaration.date,
        dateText: isoToDisplayDate(declaration.date),
        shareholderCount: declaration.shareholderCount,
        chargeText: formatGel(declaration.chargeTetri),
        citText:
            declaration.citTetri === 0
                ? NIL_AMOUNT
                : formatGel(declaration.citTetri),
        netText: formatGel(declaration.netTetri),
        dueDateText: isoToDisplayDate(declaration.dueDate),
        journalEntryId: declaration.journalEntryId,
        isUnposted: declaration.entryStatus === 'DRAFT',
    }));

// ── refusals ────────────────────────────────────────────────────────────────

/**
 * Turn a refusal into something a bookkeeper can act on.
 *
 * **No raw 400 may reach the user**, and there are three ways one can:
 *
 * 1. A **domain refusal** — each of the register's fourteen codes gets its own
 *    message under `dividends.errors.*`, because "the request was invalid" is not
 *    an answer to "why can't I declare this?". Two are worth naming here:
 *    `EXCEEDS_DISTRIBUTABLE_PROFIT`, whose remedy is a smaller distribution, and
 *    `NO_RATES_IN_FORCE`, which means no rate set governs the declaration's date
 *    and the remedy is tax settings — not a retry.
 * 2. A **journal** refusal. A dividend write that fails a *journal* rule returns
 *    the journal's code instead — `ACCOUNT_NOT_FOUND` when the chart has no
 *    account tagged for a dividend role, `AMOUNT_PRECISION` for sub-tetri money —
 *    so both vocabularies are handled and neither falls through to a generic.
 * 3. Anything else — the server's own prose, and only if there is none, a
 *    generic fallback. The server's message is preferred over a generic one
 *    wherever it exists: it names the date and the amount, which no static string
 *    can.
 *
 * An unknown code is not a failure: it falls through to the server's prose,
 * which is English but specific. A new server code shows up as an unlocalized
 * sentence rather than as "something went wrong".
 */
const JOURNAL_ERROR_CODES = ['ACCOUNT_NOT_FOUND', 'AMOUNT_PRECISION'] as const;

export const dividendErrorMessage = (
    error: Pick<ErrorResponse, 'code' | 'message' | 'errors'>,
    t: (key: string, values?: Record<string, unknown>) => string,
): string => {
    if (isDividendErrorCode(error.code)) {
        return t(`dividends.errors.${error.code}`);
    }
    if (
        typeof error.code === 'string' &&
        (JOURNAL_ERROR_CODES as readonly string[]).includes(error.code)
    ) {
        return t(`dividends.errors.${error.code}`);
    }

    if (error.message) {
        return error.message;
    }

    // ProblemDetails: `errors` is `{ FieldName: ["why"] }`. The first sentence
    // is the actionable one; joining them all produces near-duplicates.
    const fieldErrors = error.errors;
    if (fieldErrors) {
        for (const value of Object.values(fieldErrors)) {
            const first = Array.isArray(value) ? value[0] : value;
            if (typeof first === 'string' && first) {
                return first;
            }
        }
    }

    return t('dividends.errors.generic');
};
